import { useEffect, useRef } from "react";
import { EditIcon, Download, Plus, SearchIcon, XIcon } from "@/components/icons";
import { Button, Checkbox, Label } from "@/components/kit";
import { cn } from "@/lib/utils";
import type { CategoryDTO } from "@/modules/products/domain/contracts";
import { resolveCategoryIcon } from "../lib/category-icons";
import { ProductFormSection } from "./ProductFormSection";

type ProductsFilterPanelProps = {
  categories: CategoryDTO[];
  categoryId: string;
  onCategoryChange: (id: string) => void;
  searchValue: string;
  onSearchChange: (value: string) => void;
  lowStockOnly: boolean;
  onLowStockChange: (value: boolean) => void;
  canManage: boolean;
  onAddCategory: () => void;
  onEditCategory: (id: string) => void;
  onClearFilters: () => void;
  hasActiveFilters: boolean;
  onExportCsv?: () => void;
  exportingCsv?: boolean;
};

function CategoryChip({
  active,
  label,
  iconKey,
  onSelect,
  onEdit,
  chipRef,
}: {
  active: boolean;
  label: string;
  iconKey?: CategoryDTO["iconKey"];
  onSelect: () => void;
  onEdit?: () => void;
  chipRef?: (node: HTMLButtonElement | null) => void;
}) {
  const Icon = resolveCategoryIcon(iconKey);
  return (
    <div
      className={cn(
        "inline-flex shrink-0 snap-start items-center gap-0.5 rounded-full border px-1 py-0.5 transition-colors",
        active
          ? "border-secondary/40 bg-primary text-primary-foreground shadow-sm"
          : "border-secondary/20 bg-background/80 text-foreground hover:border-secondary/40 hover:bg-secondary/10",
      )}
    >
      <button
        ref={chipRef}
        type="button"
        onClick={onSelect}
        className={cn(
          "flex max-w-44 items-center gap-1.5 truncate rounded-full px-2.5 py-1.5 text-left font-medium text-sm sm:max-w-64",
          active ? "text-primary-foreground" : "text-foreground",
        )}
      >
        {iconKey ? (
          <Icon className="size-3.5 shrink-0 opacity-90" aria-hidden />
        ) : null}
        <span className="truncate">{label}</span>
      </button>
      {onEdit ? (
        <button
          type="button"
          aria-label={`ແກ້ໄຂ ${label}`}
          className={cn(
            "mr-0.5 inline-flex size-7 shrink-0 items-center justify-center rounded-full transition-colors",
            active
              ? "text-primary-foreground/80 hover:bg-primary-foreground/15 hover:text-primary-foreground"
              : "text-muted-foreground hover:bg-secondary/20 hover:text-foreground",
          )}
          onClick={(e) => {
            e.stopPropagation();
            onEdit();
          }}
        >
          <EditIcon className="size-3.5" />
        </button>
      ) : null}
    </div>
  );
}

export function ProductsFilterPanel({
  categories,
  categoryId,
  onCategoryChange,
  searchValue,
  onSearchChange,
  lowStockOnly,
  onLowStockChange,
  canManage,
  onAddCategory,
  onEditCategory,
  onClearFilters,
  hasActiveFilters,
  onExportCsv,
  exportingCsv,
}: ProductsFilterPanelProps) {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const activeChipRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    const chip = activeChipRef.current;
    const scroller = scrollerRef.current;
    if (!chip || !scroller) return;
    chip.scrollIntoView({
      behavior: "smooth",
      inline: "center",
      block: "nearest",
    });
  }, [categoryId]);

  return (
    <ProductFormSection
      icon={SearchIcon}
      title="ຄົ້ນຫາ & ກັ່ນຕອງ"
      description="ເລືອກຫມວດ ຫຼື ຄົ້ນຫາຊື່ສິນຄ້າ"
      action={
        <div className="flex flex-wrap items-center gap-2">
          {onExportCsv ? (
            <Button
              variant="outline"
              size="sm"
              className="rounded-full border-secondary/40"
              disabled={exportingCsv}
              onClick={onExportCsv}
            >
              <Download data-icon="inline-start" />
              {exportingCsv ? "ກຳລັງສົ່ງອອກ..." : "CSV"}
            </Button>
          ) : null}
          {hasActiveFilters ? (
            <Button
              variant="ghost"
              size="sm"
              className="text-muted-foreground"
              onClick={onClearFilters}
            >
              <XIcon data-icon="inline-start" />
              ລ້າງ
            </Button>
          ) : null}
          {canManage ? (
            <Button
              variant="outline"
              size="sm"
              className="rounded-full border-secondary/40"
              onClick={onAddCategory}
            >
              <Plus data-icon="inline-start" />
              ຫມວດຫມູ່
            </Button>
          ) : null}
        </div>
      }
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
          <div className="relative min-w-0 flex-1 sm:min-w-[200px]">
            <SearchIcon className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
            <input
              type="search"
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="ຄົ້ນຫາຊື່ສິນຄ້າ..."
              className="h-9 w-full rounded-md border border-input bg-background py-2 pr-3 pl-9 text-sm shadow-xs outline-none transition-[color,box-shadow] placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50"
            />
          </div>
          <div className="flex w-full items-center gap-2 rounded-xl border border-secondary/20 bg-muted/30 px-3 py-2 sm:w-auto">
            <Checkbox
              id="low-stock-only"
              checked={lowStockOnly}
              onCheckedChange={(checked) => onLowStockChange(checked === true)}
            />
            <Label htmlFor="low-stock-only" className="font-normal text-sm">
              ໃກ້ໝົດເທົ່ານັ້ນ
            </Label>
          </div>
        </div>

        <div className="space-y-1.5">
          <p className="text-muted-foreground text-xs">ຫມວດຫມູ່</p>
          <div className="relative min-w-0">
            <div
              aria-hidden
              className="pointer-events-none absolute inset-y-0 left-0 z-10 w-4 bg-linear-to-r from-card to-transparent sm:hidden"
            />
            <div
              aria-hidden
              className="pointer-events-none absolute inset-y-0 right-0 z-10 w-6 bg-linear-to-l from-card to-transparent sm:hidden"
            />
            <div
              ref={scrollerRef}
              role="tablist"
              aria-label="ຫມວດຫມູ່ສິນຄ້າ"
              className="scrollbar-thin flex snap-x snap-mandatory gap-1.5 overflow-x-auto overscroll-x-contain scroll-smooth pb-1 [-ms-overflow-style:none] [&::-webkit-scrollbar]:h-1.5"
            >
              <CategoryChip
                active={categoryId === "all"}
                label="ທັງໝົດ"
                onSelect={() => onCategoryChange("all")}
                chipRef={
                  categoryId === "all"
                    ? (node) => {
                        activeChipRef.current = node;
                      }
                    : undefined
                }
              />
              {categories.map((c) => (
                <CategoryChip
                  key={c.id}
                  active={categoryId === c.id}
                  label={c.name}
                  iconKey={c.iconKey}
                  onSelect={() => onCategoryChange(c.id)}
                  onEdit={canManage ? () => onEditCategory(c.id) : undefined}
                  chipRef={
                    categoryId === c.id
                      ? (node) => {
                          activeChipRef.current = node;
                        }
                      : undefined
                  }
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </ProductFormSection>
  );
}
